import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Link as LinkIcon, Download, ShieldCheck, Clock, Settings, Type, Pause, ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import BrandLogo from './BrandLogo';

interface LandingPageProps {
    onStart: () => void;
    onNavigate?: (page: string) => void;
}

/**
 * DynamicWord
 * Handles the smooth width morphing and text reveal/fade animations.
 */
function DynamicWord({ words }: { words: string[] }) {
    const [index, setIndex] = useState(0);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [containerWidth, setContainerWidth] = useState<number | 'auto'>('auto');
    const measureRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (words.length === 0) {
            setContainerWidth('auto');
            return;
        }

        // Initial measurement
        if (measureRef.current) {
            setContainerWidth(measureRef.current.scrollWidth);
        }

        if (words.length === 1) {
            return;
        }

        let timeoutId: number | null = null;
        const interval = window.setInterval(() => {
            // Step 1: Start fade out
            setIsFadingOut(true);

            // Step 2: After fade out completes, change word and measure new width
            timeoutId = window.setTimeout(() => {
                setIndex((prev) => (prev + 1) % words.length);
                setIsFadingOut(false);
            }, 300); // 300ms fade out duration
        }, 3000); // Change every 3s

        return () => {
            window.clearInterval(interval);
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [words.length]);

    // Measure newly set word
    useEffect(() => {
        if (measureRef.current && !isFadingOut) {
            setContainerWidth(measureRef.current.scrollWidth);
        }
    }, [index, isFadingOut]);

    return (
        <span
            className="relative inline-flex flex-col items-center justify-end mx-2 align-bottom overflow-visible"
            style={{
                width: containerWidth === 'auto' ? 'auto' : `${containerWidth}px`,
                transition: 'width 0.4s ease-in-out',
                height: '1.2em' // Ensure consistent height
            }}
        >

            {/* Invisible Measurement Layer */}
            <span
                ref={measureRef}
                className="absolute opacity-0 select-none whitespace-nowrap px-1 z-[-1] font-bold text-brand-500"
                aria-hidden="true"
            >
                {words[index] ?? ''}
            </span>

            {/* Visible Word Layer */}
            <span
                key={index}
                className={`
                    relative z-10 whitespace-nowrap px-1 font-bold text-brand-600 drop-shadow-sm
                    ${isFadingOut ? 'opacity-0 transition-opacity duration-300 ease-in' : 'opacity-100 text-reveal-in'}
                `}
                style={{
                    display: 'inline-block',
                }}
            >
                {words[index] ?? ''}
            </span>

            {/* Independent Morphing Underline */}
            <span
                className="absolute -bottom-[2px] left-0 right-0 h-[4px] bg-brand-500 rounded-full z-0 opacity-80"
                style={{
                    transition: 'all 0.4s ease-in-out'
                }}
            ></span>
        </span>
    );
}

function FaqItem({ q, a }: { q: string; a: string }) {
    const [open, setOpen] = React.useState(false);
    return (
        <div className="bg-white rounded-2xl border border-surface-border overflow-hidden">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-7 py-5 text-left gap-4"
            >
                <span className="font-sans text-base font-bold text-brand-900">{q}</span>
                <span className={`flex-shrink-0 w-6 h-6 rounded-full border border-surface-border flex items-center justify-center transition-transform duration-300 ${open ? 'rotate-45' : ''}`}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </span>
            </button>
            {open && (
                <div className="px-7 pb-6">
                    <p className="font-sans text-sm font-normal text-ink-secondary leading-relaxed">{a}</p>
                </div>
            )}
        </div>
    );
}

export default function LandingPage({ onStart, onNavigate }: LandingPageProps) {
    const { t, language } = useLanguage();

    // Opportunity Cost Calculator State
    const [teamSize, setTeamSize] = useState(30);
    const [weeklyMeetingHours, setWeeklyMeetingHours] = useState(16);
    const [hourlyRate, setHourlyRate] = useState(200);

    // Opportunity Cost Logic
    // Each 1 hour of meeting wastes 3 hours of productive time
    const totalWastedHoursPerWeek = teamSize * weeklyMeetingHours * 3;
    const weeklyCost = totalWastedHoursPerWeek * hourlyRate;
    const equivalentFTE = (totalWastedHoursPerWeek / 40).toFixed(1);

    // Derived animation intensity
    const totalActivity = teamSize + weeklyMeetingHours + (hourlyRate / 50);

    // Pre-compute random SVG data once (Fix #1: avoids Math.random() in render)
    const chaosParticles = useMemo(() =>
        Array.from({ length: 35 }, () => ({
            cx: Math.random() * 200,
            cy: Math.random() * 340 + 40,
            r: Math.random() * 2.5 + 0.5,
            fill: Math.random() > 0.5 ? '#bef264' : '#ffffff',
            opacity: Math.random() * 0.4 + 0.1,
            animCxBy: Math.random() * 100,
            animCyBy: (Math.random() - 0.5) * 50,
            durCx: `${Math.random() * 4 + 2}s`,
            durCy: `${Math.random() * 4 + 2}s`,
            durOpacity: `${Math.random() * 2 + 1.5}s`,
        })), []
    );

    const waveformBars = useMemo(() =>
        Array.from({ length: 15 }, () => ({
            width: Math.random() * 80 + 10,
            animValues: `${Math.random() * 80 + 10};${Math.random() * 80 + 10};${Math.random() * 80 + 10}`,
            dur: `${Math.random() * 1 + 0.5}s`,
        })), []
    );

    // Connection Line Refs
    const containerRef = useRef<HTMLDivElement>(null);
    const sourceRef = useRef<HTMLDivElement>(null);
    const targetRef = useRef<HTMLDivElement>(null);
    type ConnectionData = { path: string; startX: number; startY: number; endX: number; endY: number };
    const [connectionData, setConnectionData] = useState<ConnectionData | null>(null);
    const lastConnectionRef = useRef<ConnectionData | null>(null);

    useEffect(() => {
        let rafId: number;

        const shouldUpdateConnection = (nextData: ConnectionData) => {
            const prev = lastConnectionRef.current;
            if (!prev) return true;

            const epsilon = 0.5;
            return (
                Math.abs(prev.startX - nextData.startX) > epsilon ||
                Math.abs(prev.startY - nextData.startY) > epsilon ||
                Math.abs(prev.endX - nextData.endX) > epsilon ||
                Math.abs(prev.endY - nextData.endY) > epsilon
            );
        };

        const updateConnection = () => {
            if (containerRef.current && sourceRef.current && targetRef.current) {
                const containerRect = containerRef.current.getBoundingClientRect();
                const sourceRect = sourceRef.current.getBoundingClientRect();
                const targetRect = targetRef.current.getBoundingClientRect();

                const startX = sourceRect.left + sourceRect.width - containerRect.left;
                const startY = sourceRect.top + (sourceRect.height / 2) - containerRect.top;
                const endX = targetRect.left - containerRect.left;
                const endY = targetRect.top + (targetRect.height / 2) - containerRect.top;

                const distX = Math.max(Math.abs(endX - startX) * 0.45, 60);
                const cp1X = startX + distX;
                const cp1Y = startY;
                const cp2X = endX - distX;
                const cp2Y = endY;

                const nextConnectionData: ConnectionData = {
                    path: `M ${startX} ${startY} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${endX} ${endY}`,
                    startX,
                    startY,
                    endX,
                    endY,
                };

                if (shouldUpdateConnection(nextConnectionData)) {
                    lastConnectionRef.current = nextConnectionData;
                    setConnectionData(nextConnectionData);
                }
                return;
            }

            if (lastConnectionRef.current) {
                lastConnectionRef.current = null;
                setConnectionData(null);
            }
        };

        // Throttled version using requestAnimationFrame
        const throttledUpdate = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(updateConnection);
        };

        // Initial calculation
        updateConnection();

        window.addEventListener('resize', throttledUpdate);
        window.addEventListener('scroll', throttledUpdate, { capture: true, passive: true });

        const timer = window.setTimeout(updateConnection, 100);

        return () => {
            window.removeEventListener('resize', throttledUpdate);
            window.removeEventListener('scroll', throttledUpdate, true);
            cancelAnimationFrame(rafId);
            window.clearTimeout(timer);
        };
    }, []);

    // Helper for warm-toned sliders on dark prestige background
    const getSliderStyle = (value: number, min: number, max: number, type: 'gold' | 'copper' | 'rose') => {
        const percent = ((value - min) / (max - min)) * 100;

        let startColor, endColor;

        if (type === 'gold') {
            // Warm gold gradient
            startColor = '#eec78e';
            endColor = '#D4AF37';
        } else if (type === 'copper') {
            // Rich copper/bronze
            startColor = '#E8D0A9';
            endColor = '#C1A186';
        } else {
            // Dusty rose/warm pink
            startColor = '#e8b4b8';
            endColor = '#c9787e';
        }

        // Semi-transparent track for dark background
        const trackColor = 'rgba(255,255,255,0.06)';

        return {
            background: `linear-gradient(to right, ${startColor} 0%, ${endColor} ${percent}%, ${trackColor} ${percent}%, ${trackColor} 100%)`
        };
    };

    return (
        <div className="w-full flex flex-col items-center">

            {/* Hero Section */}
            <section className="w-full max-w-7xl mx-auto px-6 pt-12 md:pt-20 pb-20 flex flex-col items-center text-center">
                {/* Pill Label */}
                <div className="mb-8 md:mb-10 px-4 py-1.5 rounded-full bg-surface-border border border-surface-divider text-xs font-bold text-brand-800 uppercase tracking-wide flex items-center gap-2 shadow-sm cursor-default group/pill hover:bg-brand-50 hover:border-brand-300 hover:shadow-glow transition-all duration-500 relative overflow-hidden">
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-300/20 to-transparent translate-x-[-200%] group-hover/pill:translate-x-[200%] transition-transform duration-1000 ease-out"></span>
                    <span className="w-2 h-2 rounded-full bg-brand-800 animate-pulse group-hover/pill:bg-brand-500 transition-colors"></span>
                    <span className="font-sans font-bold text-brand-900 tracking-wider relative z-10">{t.landing.pill}</span>
                </div>
                <h1 className="text-4xl md:text-[64px] font-serif font-bold tracking-tight text-brand-900 leading-[1.2] mb-10 md:mb-12 max-w-5xl flex flex-wrap items-end justify-center">
                    {t.landing.heroPrefix}
                    <DynamicWord words={language === 'zh' ? ["复利", "结构化", "可追溯", "可执行", "高保真", "可复盘"] : ["Compounding", "Structured", "Traceable", "Actionable", "High-Fidelity", "Reviewable"]} />
                    {t.landing.heroSuffix}
                </h1>
                <p className="text-lg text-brand-700 max-w-2xl mb-16 md:mb-20 font-normal leading-relaxed">
                    {t.landing.heroSubtitle}
                </p>

                {/* Main CTA - Brand Green with Expanding Circle Hover */}
                <button
                    onClick={onStart}
                    className="group relative px-10 py-4 rounded-full bg-brand-600 text-surface-light shadow-cta-dark hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] transition-all duration-500 border border-brand-500/20 overflow-hidden"
                >
                    {/* Premium Texture Overlay */}
                    <div className="absolute inset-0 opacity-20 pointer-events-none mix-blend-overlay" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/stardust.png)` }}></div>
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"></div>

                    {/* Expanding Circle Hover Effect - Green Tones */}
                    <span className="absolute top-[120%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-0 h-0 bg-brand-300/30 rounded-full group-hover:w-[300%] group-hover:h-[600%] transition-all duration-1000 ease-out z-0 blur-xl"></span>
                    <span className="absolute top-[120%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-0 h-0 bg-brand-400/20 rounded-full group-hover:w-[250%] group-hover:h-[500%] transition-all duration-700 ease-out z-0 delay-75"></span>

                    <div className="relative z-10 flex items-center gap-3 text-lg font-medium tracking-wide">
                        <span className="group-hover:text-white transition-colors duration-300">{t.landing.cta}</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 group-hover:text-white transition-all duration-300 stroke-[2]" />
                    </div>
                </button>

                {/* High-Fidelity Chaos -> Structure Visualization */}
                <div className="mt-24 relative w-full max-w-6xl mx-auto perspective-[2000px] group text-left">
                    {/* Window Chrome - Updated Colors */}
                    <div className="relative bg-surface-light rounded-card border border-surface-border shadow-2xl overflow-hidden ring-1 ring-ink-dark/5 transform transition-transform duration-700 hover:rotate-x-1">
                        {/* Chrome Header */}
                        <div className="h-12 bg-surface border-b border-surface-hover flex items-center justify-between px-4 z-20 relative">
                            {/* Traffic Lights */}
                            <div className="flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                            </div>
                            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1 pointer-events-none">
                                <span className="text-sm font-bold text-ink-dark">SaaS公司创始人访谈</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs font-bold text-ink-secondary mr-2">
                                <span className="flex items-center gap-1.5 font-bold"><Clock size={12} /> 昨天 14:00</span>
                                <span className="flex items-center gap-1.5 font-bold"><div className="w-5 h-5 rounded-full bg-surface-hover text-ink-dark flex items-center justify-center font-bold text-[10px] border border-surface-divider">2</div> 参会人</span>
                            </div>
                        </div>

                        {/* Main Content Area */}
                        <div ref={containerRef} className="flex h-[620px] relative bg-white">

                            {/* LEFT PANEL: Raw Transcript */}
                            <div className="w-[40%] bg-surface-light border-r border-surface-hover flex flex-col relative group/left">
                                {/* NEW: Audio Player Sticky Header */}
                                <div className="p-4 border-b border-surface-hover bg-surface-light/95 backdrop-blur sticky top-0 z-10 space-y-3">
                                    <div className="bg-surface border border-surface-divider rounded-lg py-2.5 px-3 flex items-center gap-4">
                                        {/* Grounded Brown Pause */}
                                        <div className="w-9 h-9 rounded-full bg-[#5C4D47] text-white flex items-center justify-center shrink-0 shadow-sm cursor-pointer hover:bg-[#493d39] transition-colors">
                                            <Pause size={16} fill="currentColor" />
                                        </div>
                                        <div className="flex-1 space-y-1.5 mt-2">
                                            {/* Fake Waveform */}
                                            <div className="flex items-center gap-[2px] h-3.5 w-full relative">
                                                {[30, 50, 80, 40, 60, 100, 70, 90, 50, 40, 80, 60, 30, 20].map((h, i) => (
                                                    <div key={i} className={`w-full rounded-full origin-bottom ${i < 6 ? 'bg-[#C2A889]' : 'bg-[#5c4d47]/20'}`} style={{
                                                        height: `${h}%`,
                                                        animation: i < 6 ? `waveformY ${0.6 + (i % 3) * 0.2}s ease-in-out infinite` : 'none',
                                                        animationDelay: `${i * 0.1}s`
                                                    }}></div>
                                                ))}
                                            </div>
                                            <div className="flex justify-between text-[9px] font-mono text-ink-muted tracking-wide">
                                                {/* Grounded brown timestamp */}
                                                <span className="text-[#5C4D47] font-bold">17:04</span>
                                                <span>65:56</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5 space-y-6 overflow-hidden relative">
                                    {/* Gradient Mask */}
                                    <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-surface-light to-transparent z-10 pointer-events-none"></div>

                                    {/* Item 1 (Ghost) */}
                                    <div className="flex flex-col gap-1.5 opacity-50 blur-[0.5px] transition-all duration-300 hover:opacity-80 hover:blur-none">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-7 h-7 rounded-full bg-white border border-surface-divider flex items-center justify-center text-[10px] font-bold text-ink-secondary shrink-0">VC</div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold text-ink-secondary">投资合伙人</span>
                                                <span className="text-[10px] text-ink-muted">16:45</span>
                                            </div>
                                        </div>
                                        <div className="w-full pl-[38px]">
                                            <p className="text-xs text-ink-secondary leading-relaxed text-left">
                                                明白。那关于你们现在的商业化这块，特别是大客户的复购率，目前是个什么情况？因为我看你们今年 Q3 的数据，好像，呃，稍微有点波动。
                                            </p>
                                        </div>
                                    </div>

                                    {/* Item 2 (Active Source) */}
                                    <div className="relative group/leftItem p-3.5 -mx-4 rounded-xl border-[1.5px] border-transparent animate-block-breathe transition-all duration-300">

                                        <div ref={sourceRef} className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-1"></div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="flex items-center gap-2.5">
                                                {/* Grounded Brown Avatar */}
                                                <div className="w-7 h-7 rounded-full bg-[#5C4D47] border border-[#5C4D47] flex items-center justify-center text-[10px] font-bold text-white shrink-0">F</div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-[#5C4D47]">SaaS创始人</span>
                                                    <span className="text-[10px] font-semibold text-[#5C4D47]/70">17:04</span>
                                                </div>
                                            </div>
                                            <div className="w-full relative pl-[38px]">
                                                {/* Grounded Brown Text */}
                                                <p className="text-xs text-[#5C4D47] font-medium leading-relaxed text-left">
                                                    对，这里的波动主要是因为，就是... 我们的定价策略做了一次调整。原来我们是按 Seat 收费嘛，现在我们大客全部转成了包含 API 调用量的 Tier 阶梯订阅制。
                                                    虽然短期流失了几个对价格敏感的小客户，但我们头部的 NDR 其实已经做到了 120% 以上了。
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Item 3 (Ghost) */}
                                    <div className="flex flex-col gap-1.5 opacity-40 blur-[0.8px] transition-all duration-300 hover:opacity-80 hover:blur-none">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-7 h-7 rounded-full bg-white border border-surface-divider flex items-center justify-center text-[10px] font-bold text-ink-secondary shrink-0">VC</div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold text-ink-secondary">投资合伙人</span>
                                                <span className="text-[10px] text-ink-muted">18:12</span>
                                            </div>
                                        </div>
                                        <div className="w-full pl-[38px]">
                                            <p className="text-xs text-ink-secondary leading-relaxed text-left">
                                                OK，120% 的 NDR 在目前的宏观环境下确实很不错。那你们这个阶梯订阅的客单价，大概提升了多少？
                                            </p>
                                        </div>
                                    </div>

                                    {/* Item 4 (Ghost) */}
                                    <div className="flex flex-col gap-1.5 opacity-20 blur-[1px] transition-all duration-300 hover:opacity-80 hover:blur-none">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-7 h-7 rounded-full bg-white border border-surface-divider flex items-center justify-center text-[10px] font-bold text-ink-secondary shrink-0">F</div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold text-ink-secondary">SaaS创始人</span>
                                                <span className="text-[10px] text-ink-muted">18:19</span>
                                            </div>
                                        </div>
                                        <div className="w-full pl-[38px]">
                                            <p className="text-xs text-ink-secondary leading-relaxed text-left">
                                                基本客单价从原来的三万，涨到了现在平均八万多一点，这还不算后续增购的算力包。
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT PANEL: Structured Report - Clean Document Style */}
                            <div className="flex-1 bg-white flex flex-col relative overflow-hidden shadow-[inset_6px_0_20px_-10px_rgba(0,0,0,0.03)] border-l border-surface-border">
                                {/* "Template" Navigation Bar Simulator */}
                                <div className="px-6 py-3 border-b border-surface-hover flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10 gap-6">
                                    {/* Premium Template Selector (Left aligned) */}
                                    <div className="flex items-center px-3 py-1.5 rounded-full border border-surface-divider bg-[#f9faf9] cursor-pointer hover:bg-surface-hover transition-colors shadow-sm gap-1.5 flex-[0_0_auto]">
                                        <span className="text-[10px] font-bold text-ink-muted tracking-wider uppercase mt-[1px]">定制模板</span>
                                        <span className="text-sm font-serif italic text-brand-900 font-bold px-1 mt-[1px] leading-none">公司访谈</span>
                                        <ChevronDown size={14} className="text-ink-muted mt-[1px]" strokeWidth={3} />
                                    </div>

                                    <div className="flex-1 flex justify-end gap-2">
                                        {/* Formatting Tools */}
                                        <div className="hidden md:flex items-center gap-1 mr-4 border-r border-surface-divider pr-4">
                                            <div className="w-8 h-8 rounded hover:bg-surface flex items-center justify-center text-ink-secondary cursor-pointer"><Settings size={16} /></div>
                                            <div className="w-8 h-8 rounded hover:bg-surface flex items-center justify-center text-ink-secondary cursor-pointer"><Type size={16} /></div>
                                        </div>
                                        {/* Export Button - Grounded Brown theme */}
                                        <div className="px-3 py-1.5 rounded-lg bg-[#5C4D47] text-white flex items-center gap-1.5 text-xs font-semibold cursor-pointer shadow-sm hover:bg-[#493d39] transition-colors">
                                            <Download size={14} />  {'导出纪要'}
                                        </div>
                                    </div>
                                </div>

                                {/* Removed overflow-y-auto to prevent inner scrolling, reduced padding to fit the 550px height naturally */}
                                <div className="p-6 md:px-10 md:py-6 overflow-hidden relative">
                                    {/* Document Hierarchy Start */}
                                    <div className="max-w-xl mx-auto space-y-6 font-kaiti">

                                        {/* H1 */}
                                        <div>
                                            {/* Grounded header, theme brown accent prefix */}
                                            <h1 className="text-2xl font-bold text-brand-900 mb-5 tracking-tight flex items-baseline gap-2 pb-2.5 border-b border-surface-divider">
                                                <span className="font-sans text-[#5c4d47] text-xl">I.</span> 商业化与财务指标
                                            </h1>

                                            {/* H2 */}
                                            <div className="pl-4 border-l-[3px] border-surface-divider space-y-6">
                                                <div>
                                                    <h2 className="text-lg font-bold text-brand-900 mb-4 flex items-baseline gap-2">
                                                        <span className="text-ink-secondary font-sans text-sm opacity-70">1.1</span> Q3 数据波动分析与 NDR 表现
                                                    </h2>

                                                    <div className="relative group/doc mb-5">
                                                        {/* Target Highlight Box */}
                                                        <div className="rounded-xl p-4 relative border-[1.5px] border-transparent animate-block-breathe z-10">

                                                            <div ref={targetRef} className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-1"></div>

                                                            <div className="flex items-start gap-2.5 relative z-20">
                                                                <span className="text-sm font-bold text-[#C1A186] shrink-0 mt-0.5 font-sans">•</span>
                                                                <div>
                                                                    <div className="text-base font-bold text-brand-900 mb-2.5">定价策略调整至阶梯订阅制</div>
                                                                    <ul className="text-sm text-ink-dark font-medium leading-relaxed space-y-2 list-disc pl-4 marker:text-ink-tertiary">
                                                                        <li>放弃传统的按人头 <span className="font-sans">(Seat)</span> 收费模式。</li>
                                                                        <li>由于转换模式，短期内流失了部分价格敏感型小客户，导致 Q3 数据波动。</li>
                                                                        {/* Gold bold highlight underline */}
                                                                        <li><strong className="text-brand-900 border-b-2 border-[#eec78e]/60 pb-0.5">头部客户净收入留存 (NDR) 成功突破 120%。</strong></li>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* H3 Skeletons (1.1.2) */}
                                                    <div className="flex items-start gap-2.5 mb-4 px-2">
                                                        <span className="text-sm font-bold text-ink-muted shrink-0 mt-0.5 font-sans">•</span>
                                                        <div className="w-full space-y-2.5 mt-0.5">
                                                            <div className="flex items-center gap-2">
                                                                <div className="text-sm font-semibold text-ink-secondary whitespace-nowrap">客单价变化:</div>
                                                                <div className="h-5 w-3/4 bg-surface-divider/40 rounded flex-[0_0_auto]"></div>
                                                            </div>
                                                            <div className="h-3 w-1/2 bg-surface-divider/20 rounded"></div>
                                                        </div>
                                                    </div>

                                                    {/* H3 Skeletons (1.1.3) */}
                                                    <div className="flex items-start gap-2.5 mb-3 px-2">
                                                        <span className="text-sm font-bold text-ink-muted shrink-0 mt-0.5 font-sans">•</span>
                                                        <div className="w-full space-y-2.5 mt-0.5">
                                                            <div className="flex items-center gap-2">
                                                                <div className="text-sm font-semibold text-ink-secondary whitespace-nowrap">增购指标预测:</div>
                                                                <div className="h-5 w-2/3 bg-surface-divider/40 rounded flex-[0_0_auto]"></div>
                                                            </div>
                                                            <div className="pl-2 space-y-1.5 mt-2">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-1 h-1 rounded-full bg-ink-muted shrink-0"></div>
                                                                    <div className="h-3 w-full bg-surface-divider/30 rounded"></div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-1 h-1 rounded-full bg-ink-muted shrink-0"></div>
                                                                    <div className="h-3 w-4/5 bg-surface-divider/30 rounded"></div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-1 h-1 rounded-full bg-ink-muted shrink-0"></div>
                                                                    <div className="h-3 w-3/4 bg-surface-divider/30 rounded"></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* H2 Second Block */}
                                        <div className="pt-3">
                                            <div className="pl-4 border-l-[3px] border-surface-divider space-y-5">
                                                <div>
                                                    <h2 className="text-lg font-bold text-brand-900 mb-4 flex items-baseline gap-2 opacity-60">
                                                        <span className="text-ink-secondary font-sans text-sm opacity-70">1.2</span> 产品线与技术护城河
                                                    </h2>

                                                    {/* Skeletons */}
                                                    <div className="flex items-start gap-2.5 mb-3 px-2 opacity-60">
                                                        <span className="text-sm font-bold text-ink-muted shrink-0 mt-0.5 font-sans">•</span>
                                                        <div className="w-full space-y-2.5 mt-0.5">
                                                            <div className="flex items-center gap-2">
                                                                <div className="text-sm font-semibold text-ink-secondary whitespace-nowrap">核心算法迭代:</div>
                                                                <div className="h-5 w-1/3 bg-surface-divider/40 rounded flex-[0_0_auto]"></div>
                                                            </div>
                                                            <div className="pl-2 space-y-1.5 mt-2">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-1 h-1 rounded-full bg-ink-muted shrink-0"></div>
                                                                    <div className="h-3 w-5/6 bg-surface-divider/30 rounded"></div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-1 h-1 rounded-full bg-ink-muted shrink-0"></div>
                                                                    <div className="h-3 w-1/2 bg-surface-divider/30 rounded"></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Document Faded Bottom Edge */}
                                    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none z-10"></div>
                                </div>
                            </div>

                            {/* SVG CONNECTION LINE - GREEN PULSING BEAM EFFECT */}
                            <svg className="absolute inset-0 pointer-events-none z-40 overflow-visible hidden md:block w-full h-full">
                                {connectionData && (
                                    <>
                                        <defs>
                                            <filter id="glow-beam" x="-50%" y="-50%" width="200%" height="200%">
                                                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                                <feMerge>
                                                    <feMergeNode in="coloredBlur" />
                                                    <feMergeNode in="SourceGraphic" />
                                                </feMerge>
                                            </filter>
                                            <linearGradient id="activeBeam" x1="0%" y1="0%" x2="100%" y2="0%">
                                                {/* Bright, visible glow throughout, with an even brighter core */}
                                                <stop offset="0%" stopColor="#84cc16" stopOpacity="0.6" />
                                                <stop offset="25%" stopColor="#a3e635" stopOpacity="0.9" />
                                                <stop offset="50%" stopColor="#d9f99d" stopOpacity="1" />
                                                <stop offset="75%" stopColor="#a3e635" stopOpacity="0.9" />
                                                <stop offset="100%" stopColor="#84cc16" stopOpacity="0.6" />
                                            </linearGradient>
                                        </defs>

                                        {/* The Central Traceability Bi-directional Link */}
                                        <g>
                                            {/* Solid base dashed line without variable opacity */}
                                            <path d={connectionData.path} fill="none" stroke="#84cc16" strokeWidth="2" strokeDasharray="6 4" opacity="0.4" />

                                            {/* Pulsing overlay line */}
                                            <path d={connectionData.path} fill="none" stroke="url(#activeBeam)" strokeWidth="3" filter="url(#glow-beam)">
                                                <animate attributeName="stroke-dasharray" values="0 500; 500 0" dur="2s" repeatCount="indefinite" />
                                                <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" />
                                            </path>
                                        </g>

                                        {/* Left Anchor Point - Slower, Lighter */}
                                        <circle cx={connectionData.startX} cy={connectionData.startY} r="5" fill="#bef264" filter="url(#glow-beam)">
                                            <animate attributeName="r" values="5;7;5" dur="2.5s" repeatCount="indefinite" />
                                        </circle>

                                        {/* Right Anchor Point - Slower, Lighter */}
                                        <circle cx={connectionData.endX} cy={connectionData.endY} r="5" fill="#bef264" filter="url(#glow-beam)">
                                            <animate attributeName="r" values="5;7;5" dur="2.5s" repeatCount="indefinite" />
                                        </circle>
                                    </>
                                )}
                            </svg>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature Bento Grid */}
            <section className="w-full max-w-7xl mx-auto px-6 pb-32">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:auto-rows-[420px]">

                    {/* Feature 1: Expert Synthesis (Chaos in, order out - Deep Dark Theme) */}
                    <div className="lg:col-span-3 bg-[#060A04] rounded-modal p-10 overflow-hidden relative group border border-brand-800/40 transform transition-transform duration-500 hover:-translate-y-1 shadow-[0_20px_40px_-15px_rgba(26,46,5,0.6)] flex flex-col md:flex-row items-center justify-between">
                        {/* Dramatic Lighting Backdrop */}
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(132,204,22,0.15),transparent_50%)] pointer-events-none"></div>

                        {/* Text */}
                        <div className="relative z-30 w-full md:w-[45%] text-left pointer-events-none mb-8 md:mb-0 pr-6">
                            <div className="w-14 h-14 bg-[#0A1005] rounded-2xl flex items-center justify-center mb-6 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] border border-brand-700/50 text-brand-400">
                                <FileText size={28} />
                            </div>
                            <h3 className="text-2xl md:text-3xl font-serif font-bold mb-4 text-brand-50 drop-shadow-md">{t.landing.feature1Title}</h3>
                            <p className="text-brand-200/70 text-base leading-relaxed">
                                {t.landing.feature1Desc}
                            </p>
                        </div>

                        {/* Visual: SVG Chaos to Order Funnel */}
                        <div className="relative w-full md:w-[55%] h-[280px] flex items-center justify-center z-10 pointer-events-none">
                            <svg className="absolute inset-0 w-[120%] h-[120%] left-[-10%] top-[-10%]" viewBox="0 0 500 420" preserveAspectRatio="xMidYMid meet">
                                <defs>
                                    <linearGradient id="premiumFunnelGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#84cc16" stopOpacity="0" />
                                        <stop offset="50%" stopColor="#84cc16" stopOpacity="0.08" />
                                        <stop offset="100%" stopColor="#bef264" stopOpacity="0.2" />
                                    </linearGradient>
                                    <linearGradient id="neonPulseGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#84cc16" stopOpacity="0" />
                                        <stop offset="50%" stopColor="#bef264" stopOpacity="0.8" />
                                        <stop offset="100%" stopColor="#84cc16" stopOpacity="0" />
                                    </linearGradient>
                                    <linearGradient id="fadeMaskGrad" x1="0%" y1="0%" x2="80%" y2="0%">
                                        <stop offset="0%" stopColor="white" stopOpacity="0" />
                                        <stop offset="100%" stopColor="white" stopOpacity="1" />
                                    </linearGradient>
                                    <mask id="fadeMaskLeft">
                                        <rect x="0" y="0" width="500" height="420" fill="url(#fadeMaskGrad)" />
                                    </mask>
                                    <filter id="neon-glow-expert" x="-20%" y="-20%" width="140%" height="140%">
                                        <feGaussianBlur stdDeviation="4" result="blur2" />
                                        <feGaussianBlur stdDeviation="8" result="blur4" />
                                        <feMerge>
                                            <feMergeNode in="blur4" />
                                            <feMergeNode in="blur2" />
                                            <feMergeNode in="SourceGraphic" />
                                        </feMerge>
                                    </filter>
                                </defs>

                                {/* Background Funnel Shape - More Elegant curves */}
                                <path d="M 0 40 C 180 40, 260 140, 480 140 L 480 280 C 260 280, 180 380, 0 380 Z" fill="url(#premiumFunnelGrad)" opacity="0.6" />
                                <path d="M 0 40 C 180 40, 260 140, 480 140" fill="none" stroke="#84cc16" strokeWidth="1" strokeOpacity="0.3" />
                                <path d="M 0 380 C 180 380, 260 280, 480 280" fill="none" stroke="#84cc16" strokeWidth="1" strokeOpacity="0.3" />

                                {/* High-Fidelity Data Streams flowing into the Order Grid */}
                                <g mask="url(#fadeMaskLeft)">
                                    {[
                                        { y1: 80, y2: 154, dur: "2.5s", delay: "0s" },
                                        { y1: 150, y2: 194, dur: "3s", delay: "0.5s" },
                                        { y1: 250, y2: 207, dur: "2.8s", delay: "0.2s" },
                                        { y1: 340, y2: 234, dur: "3.2s", delay: "0.8s" },
                                        { y1: 110, y2: 174, dur: "3.5s", delay: "1.2s", thin: true },
                                        { y1: 290, y2: 260, dur: "2.2s", delay: "0.4s", thin: true }
                                    ].map((stream, i) => (
                                        <g key={`stream-${i}`}>
                                            <path
                                                d={`M 0 ${stream.y1} C 180 ${stream.y1}, 260 ${stream.y2}, 450 ${stream.y2}`}
                                                fill="none"
                                                stroke="url(#neonPulseGrad)"
                                                strokeWidth={stream.thin ? "1" : "2"}
                                                strokeLinecap="round"
                                                opacity={stream.thin ? "0.4" : "0.8"}
                                                filter={!stream.thin ? "url(#neon-glow-expert)" : undefined}
                                            >
                                                <animate attributeName="stroke-dasharray" values="0, 500; 200, 300" dur="5s" repeatCount="indefinite" />
                                                <animate attributeName="stroke-dashoffset" from="0" to="-500" dur={stream.dur} begin={stream.delay} repeatCount="indefinite" />
                                            </path>
                                            {/* Glowing head particles for thick streams */}
                                            {!stream.thin && (
                                                <circle r="3" fill="#ffffff" filter="url(#neon-glow-expert)">
                                                    <animateMotion dur={stream.dur} begin={stream.delay} repeatCount="indefinite" path={`M 0 ${stream.y1} C 180 ${stream.y1}, 260 ${stream.y2}, 450 ${stream.y2}`} />
                                                </circle>
                                            )}
                                        </g>
                                    ))}
                                </g>

                                {/* Structured Order Grid with Glows */}
                                <g transform="translate(360, 150)">
                                    {[0, 1, 2, 3].map((row) => (
                                        <g key={`row-${row}`} transform={`translate(0, ${row * 35})`}>
                                            {/* Background tracks */}
                                            <rect x="0" y="0" width="100" height="6" rx="3" fill="#0D150A" stroke="#84cc16" strokeWidth="1" strokeOpacity="0.2" />
                                            <rect x="0" y="10" width="70" height="4" rx="2" fill="#0D150A" stroke="#84cc16" strokeWidth="1" strokeOpacity="0.1" />

                                            {/* Filled energetic bars */}
                                            <rect x="0" y="0" width={row % 2 === 0 ? "80" : "60"} height="6" rx="3" fill="#84cc16" opacity="0.9" />
                                            <rect x="0" y="10" width={row % 2 === 0 ? "50" : "40"} height="4" rx="2" fill="#bef264" opacity="0.6" />

                                            {/* Animated scanning pulse over rows */}
                                            <rect x="0" y="0" width="20" height="6" rx="3" fill="#ffffff" filter="url(#neon-glow-expert)">
                                                <animate attributeName="x" values={`0;${row % 2 === 0 ? 60 : 40};0`} dur={`${3 + row * 0.5}s`} repeatCount="indefinite" />
                                                <animate attributeName="opacity" values="0;1;0" dur={`${3 + row * 0.5}s`} repeatCount="indefinite" />
                                            </rect>
                                        </g>
                                    ))}
                                </g>

                                {/* Chaos Particles - More erratic and glowing */}
                                <g>
                                    {chaosParticles.map((p, i) => (
                                        <circle key={`chaos-${i}`}
                                            cx={p.cx}
                                            cy={p.cy}
                                            r={p.r}
                                            fill={p.fill}
                                            opacity={p.opacity}
                                            filter="url(#neon-glow-expert)"
                                        >
                                            <animate attributeName="cx" by={p.animCxBy} dur={p.durCx} repeatCount="indefinite" />
                                            <animate attributeName="cy" by={p.animCyBy} dur={p.durCy} repeatCount="indefinite" />
                                            <animate attributeName="opacity" values="0;0.8;0" dur={p.durOpacity} repeatCount="indefinite" />
                                        </circle>
                                    ))}
                                </g>
                            </svg>
                        </div>
                    </div>

                    {/* Feature 3: High-Fidelity Deep Editing (Soft White Theme) */}
                    <div className="lg:col-span-2 bg-surface-light rounded-modal p-10 flex flex-col justify-between overflow-hidden group hover:shadow-xl transition-all relative border border-surface-divider text-brand-900">
                        <div className="relative z-10 pointer-events-none">
                            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 border border-surface-hover text-brand-700 shadow-sm">
                                <Settings size={28} />
                            </div>
                            {/* Force text-gray-900 (Black) for explicitly requested text color */}
                            <h3 className="text-2xl md:text-3xl font-serif font-bold mb-4 text-gray-900">{t.landing.feature3Title}</h3>
                            <p className="text-gray-800 text-base leading-relaxed">
                                {t.landing.feature3Desc}
                            </p>
                        </div>

                        {/* Visual: Editor & formatting to Export SVGs */}
                        <div className="mt-8 relative h-[280px] flex items-center justify-center pointer-events-none">
                            <svg className="absolute inset-0 w-[145%] h-[145%] left-[-22.5%] top-[-22.5%] overflow-visible" viewBox="0 0 320 260">
                                <defs>
                                    <filter id="shadow-edit">
                                        <feDropShadow dx="0" dy="8" stdDeviation="12" floodOpacity="0.08" />
                                    </filter>
                                    <filter id="shadow-btn">
                                        <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.1" />
                                    </filter>
                                    <linearGradient id="arrow-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#84cc16" stopOpacity="0.3" />
                                        <stop offset="100%" stopColor="#65a30d" stopOpacity="1" />
                                    </linearGradient>
                                </defs>

                                {/* Left: Document Page (Editing) */}
                                <g transform="translate(15, 60)" filter="url(#shadow-edit)">
                                    <path d="M 0 0 h 130 a 10 10 0 0 1 10 10 v 130 h -140 v -130 a 10 10 0 0 1 10 -10 z" fill="#ffffff" stroke="#E8EDDF" strokeWidth="1" />

                                    {/* Content lines */}
                                    <rect x="15" y="25" width="90" height="8" rx="4" fill="#65a30d" opacity="0.1">
                                        <animate attributeName="width" values="80;105;80" dur="5s" repeatCount="indefinite" />
                                    </rect>
                                    <rect x="15" y="45" width="110" height="5" rx="2.5" fill="#65a30d" opacity="0.05" />
                                    <rect x="15" y="58" width="95" height="5" rx="2.5" fill="#65a30d" opacity="0.05" />
                                    <rect x="15" y="71" width="100" height="5" rx="2.5" fill="#65a30d" opacity="0.05" />
                                    <rect x="15" y="84" width="80" height="5" rx="2.5" fill="#65a30d" opacity="0.05" />

                                    {/* Simulated highlighting effect */}
                                    <rect x="13" y="23" width="94" height="12" rx="4" fill="#d9f99d" opacity="0">
                                        <animate attributeName="opacity" values="0;0.5;0;0" dur="4s" begin="1s" repeatCount="indefinite" />
                                    </rect>
                                </g>

                                {/* Floating Format Toolbar */}
                                <g transform="translate(10, 20)" filter="url(#shadow-btn)">
                                    <rect width="150" height="34" rx="8" fill="#ffffff" stroke="#E8EDDF" strokeWidth="1" />

                                    {/* Hover Highlights synchronized with mouse movement (6s cycle) */}
                                    <rect x="15" y="6" width="26" height="22" rx="4" fill="#f4f5f0" opacity="0">
                                        <animate attributeName="opacity" values="0;1;1;0;0" keyTimes="0; 0.05; 0.18; 0.22; 1" dur="6s" repeatCount="indefinite" />
                                    </rect>
                                    <rect x="45" y="6" width="26" height="22" rx="4" fill="#f4f5f0" opacity="0">
                                        <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0; 0.22; 0.25; 0.36; 0.4; 1" dur="6s" repeatCount="indefinite" />
                                    </rect>
                                    <rect x="75" y="6" width="26" height="22" rx="4" fill="#f4f5f0" opacity="0">
                                        <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0; 0.38; 0.42; 0.53; 0.57; 1" dur="6s" repeatCount="indefinite" />
                                    </rect>
                                    <rect x="105" y="6" width="36" height="22" rx="4" fill="#eec78e" opacity="0">
                                        <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0; 0.55; 0.58; 0.78; 0.82; 1" dur="6s" repeatCount="indefinite" />
                                        {/* Button depress feedback at 65% (3.9s) */}
                                        <animateTransform attributeName="transform" type="scale" values="1;1;0.9;1;1" keyTimes="0; 0.63; 0.65; 0.67; 1" dur="6s" repeatCount="indefinite" />
                                    </rect>

                                    <text x="28" y="23" fontFamily="serif" fontSize="15" fontWeight="bold" fill="#1a2e05" textAnchor="middle">B</text>
                                    <text x="58" y="23" fontFamily="serif" fontSize="15" fontStyle="italic" fill="#65a30d" textAnchor="middle">I</text>
                                    <text x="88" y="23" fontFamily="sans-serif" fontSize="15" textDecoration="underline" fill="#1a2e05" textAnchor="middle">U</text>
                                    <path d="M113 14 h16 M113 17 h12 M113 20 h16" stroke="#1a2e05" strokeWidth="1.2" strokeLinecap="round" />
                                </g>

                                {/* Mouse Cursor navigating Toolbar (6s choreography) */}
                                {/* Removed local transform so animateTransform works in parent SVG coordinate space */}
                                <g>
                                    {/* 
                                        Calculated Absolute Coordinates:
                                        Toolbar offset: (10, 20)
                                        B: (10+28, 20+17) = (38, 37)
                                        I: (10+58, 20+17) = (68, 37)
                                        U: (10+88, 20+17) = (98, 37)
                                        Btn: (10+123, 20+17) = (133, 37)
                                        Start: (60, 100)
                                    */}
                                    <animateTransform attributeName="transform" type="translate" values="60 100; 38 37; 38 37; 68 37; 68 37; 98 37; 98 37; 133 37; 133 37; 60 100; 60 100" keyTimes="0; 0.083; 0.166; 0.25; 0.333; 0.416; 0.5; 0.583; 0.75; 0.833; 1" dur="6s" repeatCount="indefinite" />
                                    {/* Click burst effect perfectly timed at 65% */}
                                    <circle cx="0" cy="0" r="10" fill="none" stroke="#65a30d" strokeWidth="2" opacity="0">
                                        <animate attributeName="r" values="0;0;4;18;18" keyTimes="0; 0.64; 0.65; 0.7; 1" dur="6s" repeatCount="indefinite" />
                                        <animate attributeName="opacity" values="0;0;0.8;0;0" keyTimes="0; 0.64; 0.65; 0.7; 1" dur="6s" repeatCount="indefinite" />
                                    </circle>
                                    <path d="M0,0 L0,18 L4.5,13.5 L9,22.5 L11.25,21.3 L6.75,12.3 L13.5,12.3 Z" fill="#1a2e05" stroke="#ffffff" strokeWidth="1" filter="url(#shadow-btn)">
                                        {/* Slight scale down on click */}
                                        <animateTransform attributeName="transform" type="scale" values="1;1;0.8;1;1" keyTimes="0; 0.63; 0.65; 0.67; 1" dur="6s" repeatCount="indefinite" />
                                    </path>
                                </g>

                                {/* Middle: Animated Flowing Arrows pointing right */}
                                <g transform="translate(170, 115)">
                                    {/* Made arrows longer, reaching closer to the icons */}
                                    <path d="M 0 -30 C 25 -30, 35 -45, 65 -45" fill="none" stroke="url(#arrow-grad)" strokeWidth="2" strokeDasharray="4 4">
                                        <animate attributeName="stroke-dashoffset" from="24" to="0" dur="1s" repeatCount="indefinite" />
                                    </path>
                                    <polygon points="65,-48 70,-45 65,-42" fill="#65a30d" />

                                    <path d="M 0 0 L 65 0" fill="none" stroke="url(#arrow-grad)" strokeWidth="2" strokeDasharray="4 4">
                                        <animate attributeName="stroke-dashoffset" from="24" to="0" dur="1s" repeatCount="indefinite" />
                                    </path>
                                    <polygon points="65,-3 70,0 65,3" fill="#65a30d" />

                                    <path d="M 0 30 C 25 30, 35 45, 65 45" fill="none" stroke="url(#arrow-grad)" strokeWidth="2" strokeDasharray="4 4">
                                        <animate attributeName="stroke-dashoffset" from="24" to="0" dur="1s" repeatCount="indefinite" />
                                    </path>
                                    <polygon points="65,42 70,45 65,48" fill="#65a30d" />
                                </g>

                                {/* Right: Larger Export Icons (PDF, Word, Markdown) */}
                                <g transform="translate(235, 25)" filter="url(#shadow-btn)">
                                    {/* PDF Blob */}
                                    <g transform="translate(0, 0)">
                                        <rect width="56" height="56" rx="14" fill="#B91C1C" stroke="#ffffff" strokeWidth="2" />
                                        <path d="M 18 16 h 14 a 8 8 0 0 1 0 16 h -8 v 8 h -6 z" fill="#ffffff" opacity="0.4" />
                                        <text x="28" y="38" fill="#ffffff" fontSize="16" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">PDF</text>
                                    </g>

                                    {/* Word Blob */}
                                    <g transform="translate(0, 70)">
                                        <rect width="56" height="56" rx="14" fill="#1D4ED8" stroke="#ffffff" strokeWidth="2" />
                                        <path d="M 16 16 h 24 v 24 h -24 z" fill="#ffffff" opacity="0.2" rx="3" />
                                        <text x="28" y="38" fill="#ffffff" fontSize="24" fontWeight="bold" fontFamily="serif" textAnchor="middle">W</text>
                                    </g>

                                    {/* Markdown Blob */}
                                    <g transform="translate(0, 140)">
                                        <rect width="56" height="56" rx="14" fill="#1F2937" stroke="#ffffff" strokeWidth="2" />
                                        <text x="23" y="36" fill="#ffffff" fontSize="20" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">M</text>
                                        <path d="M 35 22 v 14 m -4 -4 l 4 4 l 4 -4" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </g>
                                </g>
                            </svg>
                        </div>
                    </div>

                    {/* Feature 4: Security (Scanner Theme) */}
                    <div className="lg:col-span-2 bg-surface-light rounded-modal p-10 flex flex-col justify-between border border-surface-divider relative overflow-hidden group hover:shadow-xl transition-all">
                        <div className="relative z-10 pointer-events-none">
                            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 border border-surface-divider text-prestige-700 shadow-sm">
                                <ShieldCheck size={28} />
                            </div>
                            {/* Force text-gray-900 (Black) for requested explicitly text color */}
                            <h3 className="text-2xl md:text-3xl font-serif font-bold mb-4 text-gray-900">{t.landing.feature4Title}</h3>
                            <p className="text-gray-800 text-base leading-relaxed">
                                {t.landing.feature4Desc}
                            </p>
                        </div>

                        {/* Visual: Polished 2D Shield/Vault */}
                        <div className="mt-8 relative h-[280px] flex items-center justify-center pointer-events-none">
                            <svg className="absolute inset-0 w-[145%] h-[145%] left-[-22.5%] top-[-22.5%] overflow-visible" viewBox="0 0 300 240">
                                <defs>
                                    <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor="#ffffff" />
                                        <stop offset="100%" stopColor="#E8EDDF" />
                                    </linearGradient>
                                    <filter id="shield-shadow">
                                        <feDropShadow dx="0" dy="12" stdDeviation="12" floodOpacity="0.06" />
                                    </filter>
                                </defs>
                                {/* Background Rings */}
                                <circle cx="150" cy="110" r="85" fill="none" stroke="#D9E2C8" strokeWidth="1" strokeDasharray="4 4" opacity="0.6">
                                    <animateTransform attributeName="transform" type="rotate" from="0 150 110" to="360 150 110" dur="20s" repeatCount="indefinite" />
                                </circle>
                                <circle cx="150" cy="110" r="55" fill="none" stroke="#D4AF37" strokeWidth="1" strokeDasharray="8 4" opacity="0.4">
                                    <animateTransform attributeName="transform" type="rotate" from="360 150 110" to="0 150 110" dur="15s" repeatCount="indefinite" />
                                </circle>

                                {/* Main Shield */}
                                <g filter="url(#shield-shadow)">
                                    <path d="M 150 30 L 220 50 L 220 130 C 220 180 150 210 150 210 C 150 210 80 180 80 130 L 80 50 Z" fill="url(#shieldGrad)" stroke="#E8D0A9" strokeWidth="3" strokeLinejoin="round" />
                                    <path d="M 150 30 L 220 50 L 220 130 C 220 180 150 210 150 210 L 150 30 Z" fill="#000000" opacity="0.03" />
                                </g>

                                {/* Core Lock */}
                                <g transform="translate(150, 110)">
                                    <rect x="-18" y="-6" width="36" height="26" rx="6" fill="#1a2e05" />
                                    <path d="M -10 -6 L -10 -15 C -10 -21, 10 -21, 10 -15 L 10 -6" fill="none" stroke="#1a2e05" strokeWidth="5" />
                                    <circle cx="0" cy="7" r="4" fill="#bef264" filter="url(#glow-blue)" />
                                </g>

                                {/* Scanline */}
                                <g>
                                    <animateTransform attributeName="transform" type="translate" values="0 -20; 0 170; 0 -20" dur="3s" repeatCount="indefinite" />
                                    <rect x="70" y="40" width="160" height="2" fill="#3b82f6" filter="url(#glow-blue)" opacity="0.8" />
                                    <rect x="70" y="0" width="160" height="40" fill="url(#scanGrad)" />
                                </g>

                                {/* Badges */}
                                <g transform="translate(10, 170)">
                                    <rect width="90" height="28" rx="8" fill="#1a2e05" stroke="#4d7c0f" />
                                    <circle cx="14" cy="14" r="4" fill="#bef264" filter="url(#glow-blue)" />
                                    <text x="26" y="18" fill="#ffffff" fontSize="10" fontWeight="bold">SOC2 TYPE II</text>
                                </g>
                                <g transform="translate(200, 20)">
                                    <rect width="80" height="28" rx="8" fill="#1a2e05" stroke="#4d7c0f" />
                                    <circle cx="14" cy="14" r="4" fill="#3b82f6" filter="url(#glow-blue)" />
                                    <text x="26" y="18" fill="#ffffff" fontSize="10" fontWeight="bold">AES-256</text>
                                </g>
                            </svg>
                        </div>
                    </div>

                    {/* Feature 2: Zero Hallucinations (Prestige Brown Theme) */}
                    <div className="lg:col-span-3 bg-prestige-900 text-prestige-50 rounded-modal p-10 relative overflow-hidden flex flex-col md:flex-row items-center justify-between group transition-all hover:shadow-2xl border border-prestige-800">
                        {/* Text Content */}
                        <div className="relative z-20 w-full md:w-[42%] flex flex-col justify-center h-full pr-8 mb-8 md:mb-0 pointer-events-none">
                            <div className="w-14 h-14 bg-prestige-800/50 rounded-2xl flex items-center justify-center mb-6 border border-prestige-700 text-prestige-300 shadow-sm">
                                <LinkIcon size={28} />
                            </div>
                            <h3 className="text-2xl md:text-3xl font-serif font-bold mb-4 text-prestige-100">{t.landing.feature2Title}</h3>
                            <p className="text-prestige-200/80 text-base leading-relaxed">
                                {t.landing.feature2Desc}
                            </p>
                        </div>

                        {/* Visual: Traceability SVG */}
                        <div className="relative w-full md:w-[58%] h-[320px] pointer-events-none">
                            <svg className="absolute inset-0 w-[110%] h-[110%] left-[-5%] top-[-5%] overflow-visible" viewBox="0 0 500 320" preserveAspectRatio="xMidYMid meet">
                                <defs>
                                    <linearGradient id="goldTrace" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#C1A186" stopOpacity="0.2" />
                                        <stop offset="50%" stopColor="#eec78e" stopOpacity="1" />
                                        <stop offset="100%" stopColor="#C1A186" stopOpacity="0.2" />
                                    </linearGradient>
                                    <filter id="glow-gold">
                                        <feGaussianBlur stdDeviation="2" result="blur" />
                                        <feMerge>
                                            <feMergeNode in="blur" />
                                            <feMergeNode in="SourceGraphic" />
                                        </feMerge>
                                    </filter>
                                </defs>

                                {/* Left Side: Audio Waveform (Raw Input) */}
                                <g transform="translate(10, 60)">
                                    <text x="0" y="-15" fill="#a89f91" fontSize="11" fontWeight="bold" letterSpacing="1">{t.landing.bento.transcriptHeader}</text>
                                    <rect x="0" y="-5" width="120" height="210" rx="8" fill="#3e3430" stroke="#634A16" strokeWidth="1" />
                                    <g transform="translate(10, 20)">
                                        {/* Dynamic Waveform bars */}
                                        {waveformBars.map((bar, i) => (
                                            <rect key={`wave-${i}`} x="0" y={i * 12} width={bar.width} height="6" rx="3" fill={i === 7 ? "#eec78e" : "#634A16"}>
                                                <animate attributeName="width" values={bar.animValues} dur={bar.dur} repeatCount="indefinite" />
                                            </rect>
                                        ))}
                                    </g>
                                    {/* Active Node */}
                                    <circle cx="106" cy="104" r="4" fill="#eec78e" filter="url(#glow-gold)">
                                        <animate attributeName="r" values="4;6;4" dur="1s" repeatCount="indefinite" />
                                    </circle>
                                </g>

                                {/* Right Side: Structured Document (Output) - SCALED UP BY 1.3 */}
                                <g transform="translate(260, -5) scale(1.3)">
                                    <text x="0" y="-15" fill="#a89f91" fontSize="10" fontWeight="bold" letterSpacing="1">{t.landing.bento.marketAnalysis}</text>
                                    <rect x="0" y="-5" width="170" height="260" rx="8" fill="#FDFDFD" stroke="#E8D0A9" strokeWidth="1" />
                                    <g transform="translate(15, 20)">
                                        <rect x="0" y="0" width="100" height="12" rx="4" fill="#3e3430" />
                                        <rect x="0" y="25" width="120" height="8" rx="4" fill="#E8EDDF" />
                                        <rect x="0" y="45" width="130" height="8" rx="4" fill="#E8EDDF" />

                                        {/* Highlighted Insight Node */}
                                        <rect x="-5" y="65" width="150" height="45" rx="6" fill="#FDF2E3" stroke="#eec78e" strokeWidth="1.5" strokeDasharray="4 2">
                                            <animate attributeName="stroke-dashoffset" from="10" to="0" dur="1s" repeatCount="indefinite" />
                                        </rect>

                                        {/* Enlarged Link Icon Badge over Highlight */}
                                        <g transform="translate(65, 48)">
                                            <rect width="80" height="22" rx="6" fill="#eec78e" />
                                            <text x="40" y="15" fill="#3e3430" fontSize="11" fontWeight="bold" textAnchor="middle">🔗 来源对应</text>
                                        </g>

                                        <rect x="5" y="80" width="80" height="8" rx="4" fill="#8B6B23" />
                                        <rect x="5" y="95" width="120" height="6" rx="3" fill="#C1A186" />

                                        <rect x="0" y="130" width="120" height="8" rx="4" fill="#E8EDDF" />
                                        <rect x="0" y="150" width="90" height="8" rx="4" fill="#E8EDDF" />
                                        <rect x="0" y="170" width="110" height="8" rx="4" fill="#E8EDDF" />
                                        <rect x="0" y="190" width="60" height="8" rx="4" fill="#E8EDDF" />
                                    </g>
                                    {/* Active Node on Doc */}
                                    <circle cx="-5" cy="105" r="4" fill="#eec78e" filter="url(#glow-gold)">
                                        <animate attributeName="r" values="4;6;4" dur="1.5s" repeatCount="indefinite" />
                                    </circle>
                                </g>

                                {/* The Central Traceability Bi-directional Link */}
                                <g>
                                    <path d="M 120 164 C 180 164, 200 140, 245 140" fill="none" stroke="#634A16" strokeWidth="2" strokeDasharray="4 4" />

                                    {/* Pulsing overlay line */}
                                    <path d="M 120 164 C 180 164, 200 140, 245 140" fill="none" stroke="url(#goldTrace)" strokeWidth="3" filter="url(#glow-gold)">
                                        <animate attributeName="stroke-dasharray" values="0 300; 300 0" dur="2s" repeatCount="indefinite" />
                                        <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" />
                                    </path>

                                    {/* Floating Validation Badge in middle */}
                                    <g transform="translate(150, 140)">
                                        <rect x="0" y="-12" width="60" height="24" rx="12" fill="#2a2320" stroke="#eec78e" strokeWidth="1" />
                                        <circle cx="12" cy="0" r="4" fill="#eec78e" filter="url(#glow-gold)" />
                                        <text x="22" y="3" fill="#eec78e" fontSize="9" fontWeight="bold">SYNC</text>
                                    </g>
                                </g>
                            </svg>
                        </div>
                    </div>

                </div>
            </section >

            {/* Testimonials (Updated to Soft White Theme) */}
            < section className="w-full py-24 bg-transparent relative z-10" >
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6 text-brand-900 tracking-tight">{t.landing.testimonialsTitle}</h2>
                        <p className="text-lg text-brand-700 font-normal leading-relaxed">{t.landing.testimonialsSub}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {t.landing.testimonials.map((testimonial, i) => (
                            <div key={i} className="bg-surface-light p-8 rounded-card border shadow-sm border-surface-border hover:shadow-deep transition-all duration-300 hover:-translate-y-1 h-full flex flex-col justify-between relative overflow-hidden group">
                                <div className="relative z-10">
                                    <div className="flex items-center gap-4 mb-6">
                                        <img
                                            src={[
                                                "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=150&h=150",
                                                "https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&q=80&w=150&h=150",
                                                "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&q=80&w=150&h=150",
                                                "https://images.unsplash.com/photo-1478760329108-5c3ed9d495a0?auto=format&fit=crop&q=80&w=150&h=150",
                                                "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=150&h=150",
                                                "https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?auto=format&fit=crop&q=80&w=150&h=150"
                                            ][i % 6]}
                                            alt={testimonial.name}
                                            className="w-12 h-12 rounded-full object-cover border border-surface-border shadow-sm group-hover:ring-2 ring-brand-200 transition-all select-none"
                                        />
                                        <div>
                                            <div className="font-serif text-lg font-bold text-brand-900 tracking-tight">
                                                {["Kevin", "Alex", "Sarah", "David", "Michael", "Emily"][i % 6]}
                                            </div>
                                            <div className="font-sans text-xs font-semibold text-brand-700 uppercase tracking-wider mt-0.5">{testimonial.role}</div>
                                        </div>
                                    </div>
                                    <p className="font-sans text-brand-900 font-normal text-base leading-relaxed">"{testimonial.text}"</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section >

            {/* Partners Wall - Updated Colors - Currently Hidden */}
            {/* <section className="w-full pb-32 pt-10">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <p className="text-sm font-semibold text-ink-muted uppercase tracking-widest mb-10">{t.landing.partnersTitle}</p>
                    <div className="flex flex-wrap justify-center gap-12 opacity-30 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-500">
                        <div className="h-8 w-32 bg-ink-dark rounded mask-logo"></div>
                        <div className="h-8 w-24 bg-ink-dark rounded mask-logo"></div>
                        <div className="h-8 w-40 bg-ink-dark rounded mask-logo"></div>
                        <div className="h-8 w-28 bg-ink-dark rounded mask-logo"></div>
                        <div className="h-8 w-32 bg-ink-dark rounded mask-logo"></div>
                    </div>
                </div>
            </section> */}

            {/* Opportunity Cost Calculator - Prestige Brown Theme */}
            < section className="w-full bg-prestige-950 py-32 rounded-t-[48px] relative overflow-hidden border-t border-prestige-900" >
                {/* Premium Halo Background - Warm Brown Theme */}
                <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-prestige-accent/8 rounded-full blur-[120px] pointer-events-none animate-blob-1"></div>
                <div className="absolute top-[20%] right-[-5%] w-[500px] h-[500px] bg-prestige-300/10 rounded-full blur-[100px] pointer-events-none animate-blob-2"></div>
                <div className="absolute bottom-[-5%] left-[30%] w-[400px] h-[400px] bg-prestige-500/8 rounded-full blur-[100px] pointer-events-none"></div>
                <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none mix-blend-overlay"></div>

                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6 tracking-tight text-prestige-100">{language === 'en' ? 'Opportunity Cost Calculator' : '机会成本计算器'}</h2>
                        <p className="text-lg text-prestige-400 font-normal leading-relaxed max-w-2xl mx-auto">{language === 'en' ? 'See how much productive time is being silently consumed.' : '看看有多少本该用于深度工作的时间，正在被无声消耗。'}</p>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-16">

                        {/* Left Side: Inputs */}
                        <div className="w-full lg:w-[35%] space-y-12 py-4">
                            <div className="space-y-6">
                                <div className="flex justify-between items-end">
                                    <label className="font-sans text-sm font-semibold text-prestige-400 uppercase tracking-wider">{language === 'en' ? 'Team Size' : '团队规模'}</label>
                                    <span className="font-data font-bold text-4xl text-prestige-100 tracking-tight">{teamSize} <span className="text-lg text-prestige-500 font-normal">{language === 'en' ? 'people' : '人'}</span></span>
                                </div>
                                <input
                                    type="range"
                                    min="1" max="100"
                                    value={teamSize}
                                    onChange={(e) => setTeamSize(parseInt(e.target.value))}
                                    style={getSliderStyle(teamSize, 1, 100, 'gold')}
                                    className="w-full h-3 rounded-full appearance-none cursor-pointer focus:outline-none colorful-slider"
                                />
                            </div>
                            <div className="space-y-6">
                                <div className="flex justify-between items-end">
                                    <label className="font-sans text-sm font-semibold text-prestige-400 uppercase tracking-wider">{language === 'en' ? 'Meeting Hours / Week' : '人均每周会议时长'}</label>
                                    <span className="font-data font-bold text-4xl text-prestige-100 tracking-tight">{weeklyMeetingHours} <span className="text-lg text-prestige-500 font-normal">{language === 'en' ? 'hrs' : '小时'}</span></span>
                                </div>
                                <input
                                    type="range"
                                    min="1" max="40"
                                    value={weeklyMeetingHours}
                                    onChange={(e) => setWeeklyMeetingHours(parseInt(e.target.value))}
                                    style={getSliderStyle(weeklyMeetingHours, 1, 40, 'copper')}
                                    className="w-full h-3 rounded-full appearance-none cursor-pointer focus:outline-none colorful-slider"
                                />
                            </div>
                            <div className="space-y-6">
                                <div className="flex justify-between items-end">
                                    <label className="font-sans text-sm font-semibold text-prestige-400 uppercase tracking-wider">{language === 'en' ? 'Hourly Rate' : '平均单人时薪'}</label>
                                    <span className="font-data font-bold text-4xl text-prestige-100 tracking-tight">¥{hourlyRate} <span className="text-lg text-prestige-500 font-normal">{language === 'en' ? '/hr' : '/时'}</span></span>
                                </div>
                                <input
                                    type="range"
                                    min="20" max="500" step="10"
                                    value={hourlyRate}
                                    onChange={(e) => setHourlyRate(parseInt(e.target.value))}
                                    style={getSliderStyle(hourlyRate, 20, 500, 'rose')}
                                    className="w-full h-3 rounded-full appearance-none cursor-pointer focus:outline-none colorful-slider"
                                />
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-prestige-accent/15 to-transparent"></div>

                        {/* Right Side: Results */}
                        <div className="flex-1 flex flex-col gap-6">

                            {/* Top Card: Weekly Hidden Cost - Hero Stat */}
                            <div className="bg-prestige-900 rounded-modal relative group shadow-prestige flex items-center justify-between border border-[#D4AF37]/30 transition-all duration-500 overflow-hidden z-10 min-h-[220px]">
                                {/* Text Container */}
                                <div className="relative z-20 p-10 flex flex-col justify-center h-full max-w-[55%]">
                                    <div className="text-[11px] text-prestige-accent font-bold uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-prestige-accent animate-pulse"></span>
                                        {language === 'en' ? 'Weekly Hidden Cost' : '每周隐形成本'}
                                    </div>
                                    <div className="flex items-baseline gap-1 mb-2">
                                        <div className="text-2xl text-prestige-accent/80 font-semibold">¥</div>
                                        <div className="font-data text-6xl md:text-7xl font-bold text-prestige-100 tracking-tight shrink-0">
                                            {weeklyCost.toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="font-sans text-prestige-400 text-xs mt-3 leading-relaxed max-w-[240px]">
                                        {language === 'en' ? 'Weekly opportunity cost lost to meeting inefficiency — misalignment, redundant recaps, and scattered action items.' : '每周因会议低效（对齐、复述、记录、追踪）而损失的机会成本。'}
                                    </div>
                                </div>

                                {/* Dynamic SVG Visualization - Gentle Waveform / Pulse */}
                                <div className="absolute right-0 top-0 h-full w-[60%] flex items-center justify-center overflow-hidden pointer-events-none">
                                    <svg className="w-full h-full" viewBox="0 0 400 200" preserveAspectRatio="xMidYMid slice">
                                        <defs>
                                            {/* Fix rectangular clipping on glowing elements */}
                                            <filter id="glow-wave" x="-20%" y="-20%" width="140%" height="140%">
                                                <feGaussianBlur stdDeviation="6" result="blur" />
                                                <feMerge>
                                                    <feMergeNode in="blur" />
                                                    <feMergeNode in="blur" />
                                                    <feMergeNode in="SourceGraphic" />
                                                </feMerge>
                                            </filter>

                                            <filter id="glow-dot" x="-50%" y="-50%" width="200%" height="200%">
                                                <feGaussianBlur stdDeviation="3" result="blur" />
                                                <feMerge>
                                                    <feMergeNode in="blur" />
                                                    <feMergeNode in="SourceGraphic" />
                                                </feMerge>
                                            </filter>

                                            {/* Mask to softly fade out edges */}
                                            <linearGradient id="fade-left-mask" x1="0%" y1="0%" x2="100%" y2="0%">
                                                <stop offset="0%" stopColor="transparent" />
                                                <stop offset="15%" stopColor="white" />
                                                <stop offset="85%" stopColor="white" />
                                                <stop offset="100%" stopColor="transparent" />
                                            </linearGradient>

                                            <mask id="tech-wave-mask">
                                                <rect x="0" y="0" width="400" height="200" fill="url(#fade-left-mask)" />
                                            </mask>
                                        </defs>

                                        <g mask="url(#tech-wave-mask)">
                                            {/* Background Subtle Grid */}
                                            <g stroke="#D4AF37" strokeWidth="0.5" opacity="0.06">
                                                <line x1="0" y1="50" x2="400" y2="50" />
                                                <line x1="0" y1="100" x2="400" y2="100" />
                                                <line x1="0" y1="150" x2="400" y2="150" />
                                                <line x1="100" y1="0" x2="100" y2="200" />
                                                <line x1="200" y1="0" x2="200" y2="200" />
                                                <line x1="300" y1="0" x2="300" y2="200" />
                                            </g>

                                            <g>
                                                {/* Gentle vertical breathing for organic tech feel */}
                                                <animateTransform attributeName="transform" type="translate" values="0,-5; 0,5; 0,-5" dur="10s" repeatCount="indefinite" />

                                                {/* --- Wave A (Main Thick Stream) --- */}
                                                <path d="M -50 120 C 100 20 300 180 450 120" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.2" />
                                                {/* Energy packet flowing along wave A */}
                                                <path d="M -50 120 C 100 20 300 180 450 120" fill="none" stroke="#eec78e" strokeWidth="3" filter="url(#glow-wave)" strokeDasharray="100 450" strokeLinecap="round" opacity="0.7">
                                                    <animate attributeName="stroke-dashoffset" from="550" to="0" dur="4s" repeatCount="indefinite" />
                                                </path>
                                                {/* Spark dot perfectly glued to Wave A */}
                                                <circle cx="0" cy="0" r="3" fill="#fff" filter="url(#glow-dot)">
                                                    <animateMotion path="M -50 120 C 100 20 300 180 450 120" dur="4s" repeatCount="indefinite" />
                                                </circle>

                                                {/* --- Wave B (Secondary Medium Stream) --- */}
                                                <path d="M -50 90 C 150 160 250 40 450 90" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.15" />
                                                <path d="M -50 90 C 150 160 250 40 450 90" fill="none" stroke="#f6d365" strokeWidth="2" filter="url(#glow-wave)" strokeDasharray="60 550" strokeLinecap="round" opacity="0.5">
                                                    <animate attributeName="stroke-dashoffset" from="610" to="0" dur="5.5s" repeatCount="indefinite" />
                                                </path>
                                                <circle cx="0" cy="0" r="2.5" fill="#fff" opacity="0.9" filter="url(#glow-dot)">
                                                    <animateMotion path="M -50 90 C 150 160 250 40 450 90" dur="5.5s" repeatCount="indefinite" />
                                                </circle>

                                                {/* --- Wave C (Tertiary Fast Data Ping) --- */}
                                                <path d="M -50 100 C 80 80 320 120 450 100" fill="none" stroke="#D4AF37" strokeWidth="0.5" opacity="0.1" />
                                                <circle cx="0" cy="0" r="1.5" fill="#fff" filter="url(#glow-dot)" opacity="0.8">
                                                    <animateMotion path="M -50 100 C 80 80 320 120 450 100" dur="2.5s" repeatCount="indefinite" />
                                                </circle>
                                            </g>
                                        </g>
                                    </svg>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6 h-48">
                                {/* Bottom Left: Weekly Wasted Hours */}
                                <div className="bg-prestige-900/60 backdrop-blur-sm p-8 rounded-modal border border-[#D4AF37]/20 shadow-sm relative overflow-hidden flex flex-col justify-between group">
                                    <div className="relative z-10">
                                        <div className="flex items-baseline gap-1">
                                            <div className="font-data text-4xl md:text-5xl font-bold text-prestige-100 mb-2 tracking-tight shrink-0">{totalWastedHoursPerWeek.toLocaleString()}</div>
                                            <div className="text-lg text-prestige-accent/60 font-semibold">{language === 'en' ? 'h' : '小时'}</div>
                                        </div>
                                        <div className="font-sans text-prestige-400 text-xs uppercase font-bold tracking-wider">{language === 'en' ? 'Weekly Time Wasted' : '每周浪费时间'}</div>
                                    </div>
                                    <div className="absolute -bottom-10 -right-6 w-48 h-48 opacity-10 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none">
                                        {/* Custom Animated Clock SVG for Time Waste */}
                                        <svg viewBox="0 0 100 100" className="w-full h-full text-prestige-accent">
                                            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="4" strokeDasharray="4 6" opacity="0.4" />
                                            <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.2" />
                                            {/* Dynamic Pie Slice representing wasted fraction */}
                                            <path d="M50 50 L50 10 A40 40 0 0 1 85 30 Z" fill="currentColor" opacity="0.3">
                                                <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur={`${Math.max(2, 8 - (totalActivity / 50))}s`} repeatCount="indefinite" />
                                                {/* Pulse the pie slice size based on wasted hours */}
                                                <animate attributeName="opacity" values="0.2;0.5;0.2" dur="3s" repeatCount="indefinite" />
                                            </path>
                                            {/* Fast spinning minute hand */}
                                            <line x1="50" y1="50" x2="50" y2="15" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                                                <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="1s" repeatCount="indefinite" />
                                            </line>
                                            {/* Slow spinning hour hand */}
                                            <line x1="50" y1="50" x2="70" y2="50" stroke="currentColor" strokeWidth="4" strokeLinecap="round">
                                                <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="12s" repeatCount="indefinite" />
                                            </line>
                                            <circle cx="50" cy="50" r="4" fill="currentColor" />
                                        </svg>
                                    </div>
                                </div>

                                {/* Bottom Right: Equivalent FTE Lost */}
                                <div className="bg-prestige-900/60 backdrop-blur-sm p-8 rounded-modal border border-[#D4AF37]/20 shadow-sm relative overflow-hidden flex flex-col justify-between group">
                                    <div className="relative z-10">
                                        <div className="flex items-baseline gap-1">
                                            <div className="font-data text-4xl md:text-5xl font-bold text-prestige-100 mb-2 tracking-tight">{equivalentFTE}</div>
                                            <div className="text-lg text-prestige-accent/60 font-semibold">FTE</div>
                                        </div>
                                        <div className="font-sans text-prestige-400 text-xs uppercase font-bold tracking-wider">{language === 'en' ? 'Weekly FTE Equivalent' : '每周等效全职人力消耗'}</div>
                                    </div>
                                    <div className="absolute -bottom-1 -right-4 w-64 h-48 opacity-40 group-hover:opacity-80 transition-opacity duration-500 pointer-events-none flex justify-end items-end">
                                        {/* Dynamic Grid of FTE Silhouettes */}
                                        {(() => {
                                            const fteValue = parseFloat(equivalentFTE);
                                            const count = Math.ceil(fteValue) || 1; // Removed 50 cap

                                            // Determine grid columns dynamically based on count.
                                            // As count gets crazy high, we pack more people into the width
                                            let cols = 3;
                                            if (count > 3) cols = 4;
                                            if (count > 8) cols = 5;
                                            if (count > 15) cols = 6;
                                            if (count > 24) cols = 8;
                                            if (count > 40) cols = 10;
                                            if (count > 60) cols = 12;
                                            if (count > 100) cols = 15;
                                            if (count > 150) cols = 20;

                                            const rows = Math.ceil(count / cols);

                                            const cellW = 55;
                                            const cellH = 75;
                                            const vbW = cols * cellW + 10;
                                            const vbH = rows * cellH;

                                            return (
                                                <svg viewBox={`0 0 ${vbW} ${vbH}`} preserveAspectRatio="xMaxYMax meet" className="w-full h-full text-prestige-accent drop-shadow-md">
                                                    <defs>
                                                        <path id="person-icon" d="M15 35c8.28 0 15-6.72 15-15S23.28 5 15 5 0 11.72 0 20s6.72 15 15 15zm0 10c-10.04 0-30 5.04-30 15v10h60V60c0-9.96-19.96-15-30-15z" />
                                                        <linearGradient id="fill-grad" x1="0%" y1="100%" x2="0%" y2="0%">
                                                            <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
                                                            <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
                                                        </linearGradient>
                                                    </defs>

                                                    {Array.from({ length: count }).map((_, i) => {
                                                        const isLast = i === count - 1;
                                                        const remainder = fteValue % 1;
                                                        let sliceFrac = 1;
                                                        if (isLast) {
                                                            if (fteValue === 0) sliceFrac = 0;
                                                            else if (remainder > 0) sliceFrac = remainder;
                                                        }

                                                        const pTop = 5;
                                                        const pHeight = 65;
                                                        const clipY = pTop + pHeight * (1 - sliceFrac);

                                                        const c = i % cols;
                                                        // Bottom-align rows to match the "flowing up" visual.
                                                        // We place row 0 at the bottom, row 1 above it, etc. (Or just top to bottom if crowd grows backwards)
                                                        // Let's do top to bottom for standard reading, but shifted visually to bottom right.
                                                        const r = Math.floor(i / cols);

                                                        const x = c * cellW + 15; // +15 shifts because person-icon x starts around -15
                                                        const y = r * cellH + 5;
                                                        const clipId = `fte-clip-${i}`;

                                                        return (
                                                            <g key={i} transform={`translate(${x}, ${y}) scale(0.9)`}>
                                                                <use href="#person-icon" fill="none" stroke="currentColor" strokeWidth="2" opacity={sliceFrac < 0.99 ? "0.2" : "0.35"} />
                                                                {(sliceFrac > 0) && (
                                                                    <>
                                                                        <clipPath id={clipId}>
                                                                            <rect x="-20" y={clipY} width="80" height={pHeight} />
                                                                        </clipPath>
                                                                        <use href="#person-icon" fill="url(#fill-grad)" clipPath={`url(#${clipId})`} opacity="0.9" />
                                                                    </>
                                                                )}
                                                            </g>
                                                        );
                                                    })}
                                                </svg>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section >

            {/* FAQ Section */}
            <section className="w-full py-24 md:py-32">
                <div className="max-w-3xl mx-auto px-6">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <h2 className="font-serif text-3xl md:text-5xl font-bold tracking-tight text-brand-900 mb-4">
                            {t.landing.faqTitle}
                        </h2>
                        <p className="font-sans text-lg font-normal text-ink-secondary leading-relaxed">
                            {t.landing.faqSub}
                        </p>
                    </div>

                    {/* FAQ Items */}
                    <div className="space-y-3">
                        {t.landing.faqs.map((item, i) => (
                            <FaqItem key={i} q={item.q} a={item.a} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            < footer className="w-full bg-surface border-t border-surface-divider" >
                {/* Main Footer Content */}
                <div className="max-w-7xl mx-auto px-6 py-16">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                        {/* Brand Column - takes more space */}
                        <div className="md:col-span-6">
                            <BrandLogo variant="dark" className="mb-6" />
                            <p className="font-sans text-sm text-ink-secondary leading-relaxed max-w-[280px]">
                                {t.landing.footerDesc}
                            </p>
                        </div>



                        <div className="md:col-span-2">
                            <h4 className="font-sans text-sm font-bold text-brand-900 mb-4">{language === 'en' ? 'Company' : '公司'}</h4>
                            <div className="flex flex-col gap-3">
                                <Link to="/about" className="font-sans text-sm text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'About' : '关于我们'}</Link>
                                <Link to="/blog" className="font-sans text-sm text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Blog' : '博客'}</Link>
                                <Link to="/careers" className="font-sans text-sm text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Careers' : '加入我们'}</Link>
                                <Link to="/contact" className="font-sans text-sm text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Contact' : '联系我们'}</Link>
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <h4 className="font-sans text-sm font-bold text-brand-900 mb-4">{language === 'en' ? 'Support' : '支持'}</h4>
                            <div className="flex flex-col gap-3">
                                <Link to="/security" className="font-sans text-sm text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Security' : '安全合规'}</Link>
                                <Link to="/privacy" className="font-sans text-sm text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Privacy' : '隐私协议'}</Link>
                                <Link to="/terms" className="font-sans text-sm text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Terms' : '服务条款'}</Link>
                            </div>
                        </div>


                        <div className="md:col-span-2">
                            <h4 className="font-sans text-sm font-bold text-brand-900 mb-4">{language === 'en' ? 'Connect' : '联系方式'}</h4>
                            <div className="flex flex-col gap-3">
                                <span className="font-sans text-sm text-ink-secondary">hi@minutes-ai.com</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar — copyright + ICP */}
                <div className="border-t border-surface-divider">
                    <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
                        <div className="font-sans text-xs text-ink-muted">© 2026 Minutes AI Inc. All rights reserved.</div>
                        <div className="font-sans text-xs text-ink-muted flex items-center gap-3">
                            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" className="hover:text-brand-700 transition-colors">沪ICP备2026002717号-1</a>
                        </div>
                    </div>
                </div>
            </footer >
        </div >
    );
}

